import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import buildInfo from './buildInfo.json'; // Import build info

function FdiRepositoryInformation({ apiBaseUrl }) {
  const { instance, accounts } = useMsal();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAccessToken = async () => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['api://3923e780-27fe-4174-a906-4659370dcd85/access_as_user'],
        account: accounts[0],
      });
      return response.accessToken;
    } catch (err) {
      console.error('Token acquisition failed:', err);
      throw err;
    }
  };

  useEffect(() => {
    const fetchInfo = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const response = await axios.get(apiBaseUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        });
        console.log('Full instance info response:', response.data);
        setInfo(response.data);
      } catch (err) {
        console.error('Fetch instance info error:', err.response || err);
        setError(`Failed to fetch instance information: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, [apiBaseUrl]);

  const renderInfo = () => {
    if (!info) return null;

    const { environment, version, lastUpdate, supportedProtocols } = info;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Repository Information</Typography>
        <Typography variant="body1">
          <strong>Environment:</strong> {environment || 'N/A'}
        </Typography>
        <Typography variant="body1">
          <strong>Version:</strong> {version || 'N/A'}
        </Typography>
        <Typography variant="body1">
          <strong>Last Update:</strong> {lastUpdate || 'N/A'}
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          <strong>Supported Protocols:</strong>
        </Typography>
        {supportedProtocols && supportedProtocols.length > 0 ? (
          <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supportedProtocols.map((protocol, index) => (
                  <TableRow key={index}>
                    <TableCell>{protocol.name || 'N/A'}</TableCell>
                    <TableCell>{protocol.scope || 'N/A'}</TableCell>
                    <TableCell>{protocol.description || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography>N/A</Typography>
        )}

        <Typography variant="h6">Build Information</Typography>
        <Typography variant="body1">
          <strong>Build Date/Time:</strong> {buildInfo.buildDateTime || 'N/A'}
        </Typography>
        <Typography variant="body1">
          <strong>Commit Hash:</strong> {buildInfo.commitHash || 'N/A'}
        </Typography>
        <Typography variant="body1">
          <strong>Commit Message:</strong> {buildInfo.commitMessage || 'N/A'}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Information
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && info && renderInfo()}

      {!loading && !error && !info && (
        <Typography sx={{ mt: 2 }}>No instance information available.</Typography>
      )}
    </Box>
  );
}

export default FdiRepositoryInformation;